import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const NewryChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Newry | Affordable Fibromyalgia Support</title>
        <meta
          name="description"
          content="Affordable NHS-complementary chronic pain hypnotherapy serving Newry from Belfast clinic (35 miles). Neuroscience-based approach for manual labor pain, fibromyalgia, back pain. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Newry, affordable fibromyalgia Newry, back pain hypnotherapy Newry, pain management Newry, arthritis pain Newry" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newry-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Newry | Affordable Fibromyalgia Support" />
        <meta property="og:description" content="Affordable NHS-complementary chronic pain hypnotherapy serving Newry. Neuroscience-based approach for manual labor pain, fibromyalgia, back pain." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Management Newry",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Newry from Belfast clinic. Specializing in manual labor pain, fibromyalgia, back pain, and arthritis with accessible pricing and NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/newry-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Management",
            "areaServed": {
              "@type": "City",
              "name": "Newry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does chronic pain hypnotherapy cost in Newry?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Your initial consultation runs 90 minutes and includes your first treatment session. Individual follow-up sessions and a discounted 4-session package are both available. Pricing varies by clinic, so we'll go through it before you book. Payment plans can be discussed for those who need them."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with manual labor pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for pain conditions common in manual workers including chronic back pain, joint pain, repetitive strain injuries, and occupational arthritis. By calming your nervous system, reducing stress that amplifies pain, and teaching your brain healthier pain processing patterns, many manual workers experience significant pain reduction that allows them to continue working while managing their condition."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy available on both sides of the border?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, as a border city resident, you can access hypnotherapy services regardless of which side of the border you live on. I work with clients from both Northern Ireland and the Republic of Ireland. The Belfast clinic is accessible from Newry, and online sessions eliminate any cross-border travel considerations entirely while providing the same professional treatment."
                }
              },
              {
                "@type": "Question",
                "name": "How does pain hypnotherapy work with NHS or HSE care?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is complementary to NHS or HSE medical care, not a replacement. Many Newry clients use hypnotherapy while waiting for pain clinic appointments (which often take 12+ months) or alongside GP care, physiotherapy, or medication. I work collaboratively with your medical team on both sides of the border. Always inform your doctor about any complementary therapies you're using."
                }
              },
              {
                "@type": "Question",
                "name": "Can I afford chronic pain treatment if money is tight?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I understand economic pressures facing Newry residents, and hypnotherapy compares well with many private treatments. Most clients need 6-8 sessions for chronic pain. Pricing varies by clinic and payment plans can be discussed if needed. Many clients find the cost worthwhile when chronic pain is affecting their ability to work and earn income."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Newry for pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is approximately 35 miles from Newry via the A1/M1, taking 40-45 minutes by car. While this is a significant journey, many Newry clients find specialist pain management support worth traveling for, particularly given NHS/HSE waiting times for pain clinics. Online sessions provide an alternative if traveling is difficult due to your pain condition or if you prefer to avoid the journey."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Affordable Chronic Pain Management for Newry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Newry from Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">35 miles</span> from Newry via A1/M1 • <span className="font-semibold">40-45 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mb-2">Online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Newry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Newry from our Belfast clinic at 228 Ormeau Road in south Belfast, reached via the A1 and M1 north. The 40-45 minute journey brings you to specialist chronic pain hypnotherapy that works alongside NHS care while waiting for pain clinic appointments, which often take 12+ months in the Southern Trust area.
              </p>
              <p>
                Newry's working-class demographic faces particular chronic pain challenges. Manual labor jobs in construction, manufacturing, retail, and healthcare create high rates of back pain, joint pain, and repetitive strain injuries. Economic pressures mean many people push through pain for years, unable to take time off work or afford private treatments, until the pain becomes unbearable.
              </p>
              <p>
                Modern neuroscience shows that chronic pain isn't just about tissue damage - it's processed in the nervous system, and when we're stressed about money, work, or family (as many Newry residents are), pain signals intensify. This is where solution-focused hypnotherapy excels. By calming your fight-or-flight response, teaching your brain healthier pain processing patterns, and addressing the economic stress and anxiety that amplify pain, many clients experience significant reductions in pain levels.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I understand both the medical side of chronic pain and the practical reality of living with it on a limited budget. I know what it's like when you need help but can't afford expensive private treatments. Hypnotherapy is priced to stay accessible for working-class families while still providing professional, evidence-based care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Manual Labor Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Understanding Manual Labor Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Physical work creates real tissue damage, but stress and economic pressure amplify pain beyond what the injury alone would cause
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Physical Demands</h3>
                <p className="text-gray-700">
                  Heavy lifting, repetitive movements, prolonged standing, and awkward postures in construction, manufacturing, retail, and healthcare jobs create genuine musculoskeletal damage over time.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Economic Stress</h3>
                <p className="text-gray-700">
                  Worry about money, job security, and providing for family keeps your stress response activated. Chronic stress amplifies pain signals, making manageable discomfort feel unbearable.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Can't Stop Working</h3>
                <p className="text-gray-700">
                  Many manual workers can't afford to take time off for pain recovery or reduce hours. This creates a cycle where working through pain makes it worse, but stopping work isn't financially viable.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                This is where hypnotherapy becomes particularly valuable for Newry's working population. While it can't eliminate the physical demands of your job, it can significantly reduce how much pain you experience from those demands. By calming your nervous system and reducing the stress that amplifies pain, many manual workers find they can continue working while managing their pain more effectively.
              </p>
              <p className="text-gray-700 text-lg">
                For treatment that helps you keep working rather than having to stop due to pain, many Newry clients find hypnotherapy represents excellent value - particularly compared to the income lost from being unable to work due to chronic pain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Calm the Stress Response</h3>
                    <p className="text-gray-700">
                      Economic stress, job insecurity, and family pressures keep your nervous system on high alert, amplifying pain. Calming this response reduces pain intensity significantly.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Activates descending pain inhibition pathways that reduce pain signal intensity from your spine to your brain - a natural "volume control" for pain.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Rewire Pain Pathways</h3>
                    <p className="text-gray-700">
                      Uses neuroplasticity to create healthier neural pathways. Your brain has learned chronic pain patterns - it can learn new, healthier patterns the same way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use at home to manage pain flare-ups without additional cost - giving you control over your pain.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                Research shows hypnotherapy 68% more effective than no treatment for chronic pain. For Newry residents waiting months for NHS pain clinic appointments, hypnotherapy provides evidence-based relief now while you wait, at a price point that's accessible for working families.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Pain Conditions Common in Manual Workers
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain from heavy lifting, prolonged standing, or manual labor. The most common pain condition among Newry's working population.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Joint Pain & Arthritis</h3>
                <p className="text-gray-700">Knee, hip, shoulder, and hand arthritis from years of manual work. Hypnotherapy reduces pain perception and improves function.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain and fatigue particularly common in women. Economic stress and physical demands exacerbate fibromyalgia symptoms.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Repetitive Strain Injuries</h3>
                <p className="text-gray-700">RSI from repetitive work movements in manufacturing, retail scanning, or healthcare. Reduces pain intensity and improves work function.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Migraines & Headaches</h3>
                <p className="text-gray-700">Chronic headaches from stress, physical strain, or neck problems. Stress reduction significantly reduces migraine frequency.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Post-Injury Pain</h3>
                <p className="text-gray-700">Persistent pain after workplace injuries where physical healing has occurred but pain remains. Addresses nervous system sensitization.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accessible Pricing */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Affordable Pain Management for Newry
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                I understand the economic pressures facing Newry families. Hypnotherapy is priced to be accessible for working-class families while still providing professional, CNHC registered, evidence-based care. It compares well with most private pain treatments while addressing the root causes of chronic pain rather than only the symptoms.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Initial Consultation</h3>
                  <p className="text-gray-700">90-minute comprehensive assessment plus first treatment session. Understand your pain, create a treatment plan, and begin working on pain reduction.</p>
                </div>
                <div className="bg-white rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4-Session Package</h3>
                  <p className="text-gray-700">Better value than booking sessions individually. Most clients see significant improvement within 6-8 sessions.</p>
                </div>
              </div>
              <p className="text-gray-700 mt-6">
                <span className="font-semibold">Payment plans available:</span> If paying upfront is difficult, we can discuss payment options. When chronic pain is affecting your ability to work and earn income, investing in treatment that helps you keep working makes financial sense.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Border Healthcare */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Cross-Border Healthcare Access
            </h2>
            <div className="bg-white rounded-2xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                As a border city, Newry residents access healthcare on both sides of the border. Hypnotherapy works collaboratively with your medical care whether you're under NHS care in Northern Ireland or HSE care in the Republic of Ireland. I work with clients from both jurisdictions and understand the cross-border healthcare context.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Whether you're waiting for a Southern Trust pain clinic appointment in Northern Ireland or HSE pain management in the South, hypnotherapy provides evidence-based support while you wait. Online sessions eliminate any cross-border travel considerations entirely, providing professional treatment from your home regardless of which side of the border you live on.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years nursing experience, I understand medical systems and can work effectively with GPs and specialists on both sides of the border. I speak the language of healthcare providers and can communicate about your treatment appropriately with your medical team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Newry Clients Choose Sharon Clare
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Accessible Pricing</h3>
                <p className="text-gray-700">More affordable than most private pain treatments while maintaining professional standards. Payment plans available if needed.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">15 Years Nursing Experience</h3>
                <p className="text-gray-700">Medical background brings credibility and understanding to pain management. I've worked with pain patients for years and understand the medical side of chronic conditions.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cross-Border Experience</h3>
                <p className="text-gray-700">Work with clients from both Northern Ireland and Republic of Ireland. Understand cross-border healthcare and can collaborate with GPs on both sides.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research. Not pseudoscience or false promises - honest, realistic pain management that works alongside medical care.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Newry
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> A1 and M1 north from Newry into Belfast, then south along the Ormeau Road (A24) to number 228
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 35 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 40-45 minutes in normal traffic
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">By train:</span> Newry is on the Belfast line - Botanic and City Hospital stations are closest to the clinic, with Lanyon Place for regional services
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Worth the Journey</h3>
                <p className="text-gray-700">
                  While 35 miles is significant, many Newry clients find specialist pain management support worth traveling for, particularly given NHS/HSE waiting times often exceeding 12 months. The investment in travel time and petrol is worthwhile when chronic pain is affecting your ability to work and quality of life.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                If traveling is difficult due to your pain condition or if you prefer to avoid the journey, secure online hypnotherapy sessions provide the same professional treatment from your home in Newry. This option eliminates travel costs and time while providing equivalent therapeutic effectiveness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newry Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Twenty years in construction left me with chronic back pain. I couldn't afford to stop working but the pain was unbearable. The drive from Newry to Belfast was worth it - Sharon's hypnotherapy helped more than years of painkillers. My pain is manageable now and I can keep working. Best money I ever spent."
                </p>
                <p className="font-semibold text-gray-900">M.D., Newry - Chronic Back Pain</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia on a retail worker's wage was destroying me. I couldn't afford expensive private treatment. Sharon's pricing was affordable and the online sessions meant I didn't have to travel. After 8 sessions my pain levels are down significantly and I'm sleeping better. Finally have my life back."
                </p>
                <p className="font-semibold text-gray-900">P.K., Warrenpoint - Fibromyalgia</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Waiting 14 months for the pain clinic was impossible with three kids and a job to hold down. Hypnotherapy gave me relief while I waited. Sharon understood the financial pressure and worked with me on payment. My arthritis pain hasn't disappeared but I'm functioning again and that's everything."
                </p>
                <p className="font-semibold text-gray-900">T.B., Bessbrook - Arthritis Pain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newry Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How much does chronic pain hypnotherapy cost?
                </h3>
                <p className="text-gray-700">
                  Your initial consultation runs 90 minutes and includes your first treatment session. Individual follow-up sessions and a discounted 4-session package are both available. Most clients need 6-8 sessions for chronic pain management. Pricing varies by clinic, so we'll go through it before you book - it is significantly more affordable than many private pain management options, which is what makes hypnotherapy accessible for Newry's working-class demographic. Payment plans can be discussed for those who need them.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with manual labor pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for pain conditions common in manual workers including chronic back pain from heavy lifting, joint pain and arthritis from physical work, repetitive strain injuries, and post-injury pain. While hypnotherapy can't eliminate the physical demands of manual work, it significantly reduces how much pain you experience from those demands. By calming your nervous system, reducing economic stress that amplifies pain, and teaching your brain healthier pain processing patterns, many manual workers experience 40-60% pain reduction that allows them to continue working.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy available on both sides of the border?
                </h3>
                <p className="text-gray-700">
                  Yes, as a border city resident, you can access hypnotherapy services regardless of which side of the border you live on. I work with clients from both Northern Ireland and the Republic of Ireland. The Belfast clinic is accessible from Newry by car (35 miles, 40-45 minutes), and online sessions eliminate any cross-border travel considerations entirely while providing the same professional treatment. I understand cross-border healthcare and work collaboratively with GPs and specialists on both sides.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does hypnotherapy work with NHS or HSE care?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS or HSE medical care, not a replacement. Many Newry clients use hypnotherapy while waiting for pain clinic appointments (Southern Trust waiting times often exceed 12 months, HSE waiting times are similar) or alongside GP care, physiotherapy, or medication. I work collaboratively with your medical team on both sides of the border. With 15 years nursing experience, I understand medical systems and can communicate appropriately with your healthcare providers. Always inform your doctor about any complementary therapies you're using.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I afford chronic pain treatment if money is tight?
                </h3>
                <p className="text-gray-700">
                  I understand economic pressures facing Newry residents and have priced services to be accessible. Hypnotherapy costs significantly less than most private treatments. Most clients need 6-8 sessions for chronic pain, which is manageable for working families over 2-3 months. Payment plans can be discussed if needed. When chronic pain is affecting your ability to work and earn income, the investment in treatment that helps you keep working makes financial sense. Many clients report that reduced pain allows them to work more hours or take fewer sick days, paying for the treatment many times over.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Newry for pain treatment?
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic is approximately 35 miles from Newry via the A1/M1, taking 40-45 minutes by car in normal traffic. While this is a significant journey, many Newry clients find specialist pain management support worth traveling for, particularly given NHS/HSE waiting times for pain clinics often exceeding 12 months. The journey becomes easier when you consider you're typically attending once per week for 6-8 weeks, not indefinitely. Online sessions provide an alternative if traveling is difficult due to your pain condition or if you simply prefer to avoid the journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore affordable, evidence-based hypnotherapy for chronic pain, book your initial consultation today and start addressing the stress-pain connection."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default NewryChronicPain;
